import { findIndex } from "@dhx/ts-common/core";
import { el, KEYED_LIST, VNode } from "@dhx/ts-common/dom";
import { getHeight, getWidth } from "../helpers/cells";
import { getTotalWidth, isHtmlEnable, getTotalHeight } from "../helpers/main";
import { GridEvents, ICol, ICoords, IRendererConfig, GridSystemEvents, IGridConfig, ISpan } from "../types";
import { getEditor } from "./editors/editors";
import { locateNodeByClassName } from "@dhx/ts-common/html";
import { getTreeCellWidthOffset, toFormat } from "../helpers/data";

type mouseEvents =
	| GridEvents.cellClick
	| GridEvents.cellMouseOver
	| GridEvents.cellMouseDown
	| GridEvents.cellDblClick
	| GridEvents.cellRightClick;

type touchEvents = GridSystemEvents.cellTouchEnd | GridSystemEvents.cellTouchMove;

function handleMouse(
	rowStart: number,
	colStart: number,
	conf: IRendererConfig,
	type: mouseEvents & touchEvents,
	e: any
) {
	colStart = locateNodeByClassName(e.target, "dhx_grid-fixed-cols-wrap") ? 0 : colStart;
	const target = locateNodeByClassName(e.target, "dhx_grid-cell");
	const targetSpan = locateNodeByClassName(e.target, "dhx_span-cell");

	if ((!target && !targetSpan) || !type) {
		return;
	}

	const rowNode = target ? target.parentNode : targetSpan;

	const colId = (target || targetSpan).getAttribute("data-dhx-col-id");
	const columns = conf.columns.filter(col => !col.hidden);
	const col = columns.find(column => column.id === colId);

	const rowId = (rowNode as VNode).getAttribute("data-dhx-id");
	const row = conf.data.find(r => r.id.toString() === rowId);

	const systemEvent = (type as string).toLocaleLowerCase().includes("touch");

	if (systemEvent) {
		conf._events.fire(type, [row, col, e]);
	} else {
		conf.events.fire(type, [row, col, e]);
	}
}

export function getHandlers(row: number, column: number, conf: IRendererConfig) {
	return {
		onclick: [handleMouse, row, column, conf, GridEvents.cellClick],
		onmouseover: [handleMouse, row, column, conf, GridEvents.cellMouseOver],
		onmousedown: [handleMouse, row, column, conf, GridEvents.cellMouseDown],
		ondblclick: [handleMouse, row, column, conf, GridEvents.cellDblClick],
		oncontextmenu: [handleMouse, row, column, conf, GridEvents.cellRightClick],
		ontouchstart: [handleMouse, row, column, conf, GridEvents.cellMouseDown],
		ontouchmove: [handleMouse, row, column, conf, GridSystemEvents.cellTouchMove],
		ontouchend: [handleMouse, row, column, conf, GridSystemEvents.cellTouchEnd],
	};
}

export function getTreeCell(content: any, row: any, col: ICol, conf: IRendererConfig) {
	const getCellAriaAttrs = (col, ind) => ({
		role: "gridcell",
		"aria-colindex": ind,
	});
	const getToggleAriaAttrs = row => ({
		role: "button",
		"aria-label": row.$opened ? "Collapse group" : "Expand group",
	});
	const isEditable = conf.$editable && conf.$editable.row === row.id && conf.$editable.col === col.id;
	let css = "";
	const cellAlign = col.align ? ` dhx_align-${col.align}` : "dhx_align-left";
	if (conf.dragMode && conf.dragItem === "row") {
		css +=
			(row.$drophere && !isEditable ? " dhx_grid-cell--drophere" : "") +
			(row.$dragtarget && !isEditable ? " dhx_grid-cell--dragtarget" : "") +
			(!isEditable ? " dhx_grid-cell--drag" : "");
	}

	const parentPadding = getTreeCellWidthOffset(row);
	return el(
		".dhx_grid-cell",
		{
			class:
				`dhx_tree-cell ${col.$cellCss[row.id] || ""} ${row.$items ? "dhx_grid-expand-cell" : ""}` +
				` ${isEditable ? "dhx_tree-editing-cell" : ""} ${css}` +
				cellAlign,
			style: {
				width: col.$width,
				height: row.$height,
				padding: !row.$items ? `0 0 0 ${parentPadding}px` : 0,
			},
			"data-dhx-col-id": col.id,
			...getCellAriaAttrs(col, 1),
		},
		[
			row.$items
				? el(".dhx_grid-expand-cell-icon", {
						class: row.$opened ? "dxi dxi-chevron-up" : "dxi dxi-chevron-down",
						"data-dhx-id": row.id,
						...getToggleAriaAttrs(row),
						style: {
							padding: row.$level ? `0 0 0 ${4 + parentPadding}px` : "0 0 0 4px",
						},
				  })
				: null,
			el(
				".dhx_tree-cell",
				{
					class: cellAlign + `${(conf.autoHeight && " dhx_tree-cell_auto-height") || ""}`,
				},
				[content]
			),
		]
	);
}

function getEditorCell(row: any, col: ICol, conf: IRendererConfig) {
	return getEditor(row, col, conf);
}

export function getCells(conf: IRendererConfig): any[] {
	if (!conf.data || !conf.columns) {
		return [];
	}
	const getRowAriaAttrs = ind => ({
		role: "row",
		"aria-rowindex": ind,
	});
	const pos = conf.$positions;
	const data = conf.data ? conf.data.slice(pos.yStart, pos.yEnd) : [];
	const columns = conf.columns.slice(pos.xStart, pos.xEnd);
	let selectedCell = conf.selection.getCell();
	let isFirstTabindex = true;
	return data.map((row, index) => {
		const isLastRow = data.length - 1 === index;
		let rowCss = "";

		if (conf.rowCss) {
			rowCss = conf.rowCss(row);
		}
		if (row.$css) {
			rowCss += row.$css;
		}

		return el(
			".dhx_grid-row",
			{
				style: { height: isLastRow ? row.$height + 1 : row.$height },
				"data-dhx-id": row.id,
				class: rowCss,
				_key: row.id,
				_flags: KEYED_LIST,
				...getRowAriaAttrs(pos.yStart + index + 1),
			},
			row.$customRender
				? [row.$customRender(row, conf)]
				: columns.map((col, colIndex) => {
						if (!col.hidden) {
							let initValue = row[col.id];
							if (
								(col.editable || (conf.editable && col.editable !== false)) &&
								(col.editorType === "select" ||
									col.editorType === "combobox" ||
									col.editorType === "multiselect") &&
								col.options
							) {
								initValue =
									typeof initValue === "string" && col.editorType === "multiselect"
										? initValue.split(",").map(item => item.trim())
										: [initValue?.toString()];
								initValue = initValue
									.map(item => {
										return (
											col.options.find(
												option => option.id && option.id.toString() === item
											)?.value ?? item
										);
									})
									.join(", ");
							}
							const value = toFormat(initValue, col.type, col.format);
							const getTabIndex = (col, row) => {
								const attrs = {
									tabindex: -1,
								};

								if (selectedCell) {
									// is current cell selected?
									if (selectedCell.row.id === row.id && selectedCell.column.id === col.id) {
										attrs["tabindex"] = 0;
									}
								} else if (isFirstTabindex) {
									attrs["tabindex"] = 0;

									// one-time event - only on first focus
									// FIXME: crutch-solution
									attrs["onfocus"] = e => {
										// FIXME: issue with samples that already setCell from index.html, rendered twice
										// TODO: not here BUT if first cell is not in viewport -> scroll to it to set focus
										if (conf.selection && !selectedCell) {
											const rowId = e.target.parentNode.getAttribute("data-dhx-id");
											const colId = e.target.getAttribute("data-dhx-col-id");
											if (colId && rowId) {
												conf.selection.setCell(rowId, colId);
												selectedCell = conf.selection.getCell();
											}
										}
									};
								}

								isFirstTabindex = false;

								return attrs;
							};
							const getEditBtnAriaAttrs = () => ({
								role: "button",
								"aria-label": "Edit content",
							});
							const getCellAriaAttrs = (col, colIndex, rowIndex, isEditable) => ({
								role: "gridcell",
								"aria-colindex": colIndex,
								"aria-readonly": isEditable ? "false" : "true",
								// "aria-selected": isSelected ? "true" : "false", // TODO: add
								...getTabIndex(col, row),
							});
							const defaultTemplate = (text: any) => {
								if (typeof text === "boolean" || col.type === "boolean") {
									if (typeof text !== "string") {
										return `${Boolean(text)}`;
									}
								}
								return text || text === 0 ? text : "";
							};

							let content = col.template
								? col.template(value, row, col)
								: defaultTemplate(value);
							// content can be a domvm node or a string
							if (typeof content === "string") {
								content = isHtmlEnable(conf, col)
									? el("div.dhx_grid-cell__content", {
											".innerHTML": content,
											...getEditBtnAriaAttrs(),
									  })
									: content;
							}

							let css = `${(col.$cellCss && col.$cellCss[row.id]) || ""} dhx_${
								col.type
							}-cell`.replace(/\s+/g, " ");

							let colWidth = col.$width;
							const isEditable =
								conf.$editable &&
								conf.$editable.row === row.id &&
								conf.$editable.col === col.id &&
								!conf.$editable.isSpan;
							const { leftSplit, columns } = conf;
							if (
								isEditable ||
								(col.type === "boolean" &&
									((conf.editable && (col.editable ?? true)) ||
										(!conf.editable && col.editable)))
							) {
								content = getEditorCell(row, col, conf).toHTML();
								css += " dhx_grid-cell__editable";
								if (leftSplit === columns.indexOf(col) + 1) {
									colWidth -= 1;
								}
							}

							if (conf.type === "tree" && conf.firstColId === col.id) {
								return getTreeCell(content, row, col, conf);
							}

							if (conf.dragMode && conf.dragItem === "row") {
								css +=
									(row.$drophere && !isEditable ? " dhx_grid-cell--drophere" : "") +
									(row.$dragtarget && !isEditable ? " dhx_grid-cell--dragtarget" : "") +
									(!isEditable ? " dhx_grid-cell--drag" : "");
							}
							if (col.align) {
								css += ` dhx_align-${col.align}`;
							}
							if (isHtmlEnable(conf, col)) {
								css += ` dhx_grid-cell__content_html-enable`;
							}
							if (conf.autoHeight) {
								css += " dhx_grid-cell__content_auto-height";
							}

							return el(
								".dhx_grid-cell",
								{
									class: css,
									style: {
										width: colWidth,
										height: row.$height + "px",
									},
									_key: col.id,
									"data-dhx-col-id": col.id,
									...getCellAriaAttrs(col, pos.xStart + colIndex + 1, index, conf.editable),
								},
								[content]
							);
						}
				  })
		);
	});
}

export function getSpans(config: IRendererConfig, frozen?: boolean): any[] {
	const spanCells = [];
	const { $positions: pos, columns, data: rows, spans: rSpans } = config;

	if (!columns.length || !rSpans) return null;
	const filteredSpans = rSpans.filter((span: any) => span.$renderFrom?.includes(config.$renderFrom));

	const spans = filteredSpans.sort((a, b) => {
		return typeof a.row === "string" && typeof b.row === "string"
			? a.row.localeCompare(b.row)
			: (a.row as number) - (b.row as number);
	});

	for (let i = 0; i < spans.length; i++) {
		const row = spans[i].row;
		const col = spans[i].column;
		const spanHeight = spans[i].rowspan;
		const spanWidth = spans[i].colspan;
		const spanCss = spans[i].css;
		const markCss = spans[i].$markCss;

		if (spanHeight === 1) {
			continue;
		}

		const colIndex = findIndex(columns, item => `${item.id}` === `${col}`);
		const rowIndex = findIndex(rows, item => `${item.id}` === `${row}`);

		if (colIndex < 0 || rowIndex < 0) {
			continue;
		}

		const currCol = columns[colIndex];
		const currRow = rows[rowIndex];
		if (currCol.hidden) {
			continue;
		}

		let content = spans[i].text ?? (currRow[col] === undefined ? "" : currRow[col]);

		const t = (text: any, _row: any, _col: any) => (text || text === 0 ? text : "");
		const template = currCol.template || t;

		content = template(content, currRow, currCol);
		content =
			typeof content === "string"
				? el("div.dhx_span-cell-content", { ".innerHTML": content })
				: content;

		let currentTop = 0;
		for (let index = 0; index < rowIndex; index++) {
			currentTop += rows[index].$height;
		}
		const top = currentTop - (frozen ? 0 : 1);

		let left = 0;
		for (let s = colIndex - 1; s >= 0; s--) {
			left += columns[s].$width;
		}
		const isExpandingSpan = currRow.$items && colIndex === 0;
		const rowspanWithLastCol = colIndex === columns.length - 1;
		const colspanWithLastCol = colIndex + spanWidth === columns.length;

		let css = currCol.header[0].text ? " dhx_span-cell" : " dhx_span-cell dhx_span-cell--title";
		css += isExpandingSpan ? " dhx_span-expand-cell" : "";
		css += markCss ? ` ${markCss}` : "";
		css += spanCss ? ` ${spanCss}` : "";
		css += rowIndex === 0 ? " dhx_span-first-row" : "";
		css += colIndex === 0 ? " dhx_span-first-col" : "";
		css += rowspanWithLastCol || colspanWithLastCol ? " dhx_span-last-col" : "";
		css += !spanWidth ? ` dhx_span-${currCol.type || "string"}-cell` : ` dhx_span-string-cell`;
		css += currCol.align ? ` dhx_align-${currCol.align}` : ` dhx_align-left`;

		let width = spanWidth > 1 ? getWidth(columns, spanWidth, colIndex) : currCol.$width;
		const height = spanHeight > 1 ? getHeight(rows, spanHeight, rowIndex) : currRow.$height;

		const isEditable = config.$editable && config.$editable.row === row && config.$editable.col === col;
		if (
			isEditable ||
			(currCol.type === "boolean" &&
				((config.editable && (currCol.editable ?? true)) || (!config.editable && currCol.editable)))
		) {
			const { leftSplit, topSplit } = config;
			const fixedByCol = leftSplit && colIndex < leftSplit;
			const fixedByRow = topSplit && rowIndex < topSplit;
			const isFixed = fixedByCol || fixedByRow;
			if (!isFixed) {
				const text = spans[i].text as string;
				content = getEditorCell(currRow, currCol, config).toHTML(text);
				css += " dhx_span__editable";
				if (leftSplit === colIndex + 1) {
					width -= 1;
				}
			}
		}

		spanCells.push(
			el(
				"div",
				{
					class: css,
					style: {
						width,
						height,
						top,
						left,
					},
					"data-dhx-col-id": col,
					"data-dhx-id": row,
					"aria-hidden": "true",
					...getHandlers(pos.yStart, pos.xStart, config),
				},
				[
					isExpandingSpan
						? el(".dhx_span-expand-cell-icon", {
								class: currRow.$opened ? "dxi dxi-chevron-up" : "dxi dxi-chevron-down",
								"data-dhx-id": currRow.id,
								role: "button",
								"aria-label": currRow.$opened ? "Collapse group" : "Expand group",
								style: {
									padding: `0 0 0 ${4 + getTreeCellWidthOffset(currRow)}px`,
								},
						  })
						: null,
					content,
				]
			)
		);
	}
	return spanCells;
}

export function getShifts(conf: IRendererConfig): ICoords {
	const columnsLeft = conf.columns.slice(0, conf.$positions.xStart);
	const rowsTop = conf.data.slice(0, conf.$positions.yStart);
	return {
		x: getTotalWidth(columnsLeft),
		y: getTotalHeight(rowsTop),
	};
}

export function normalizeSpan(span: ISpan, renderConfig: IGridConfig) {
	const { leftSplit = 0, topSplit = 0, data, columns } = renderConfig;
	const { column, row, colspan, rowspan } = span;
	const colIndexStart = columns.findIndex(c => c.id == column);
	const rowIndexStart = data.findIndex(r => r.id == row);
	const colIndexEnd = colIndexStart + (colspan || 0);
	const rowIndexEnd = rowIndexStart + (rowspan || 0);
	const $renderFrom: string[] = [];
	// if the span is not fully fixed
	if (colIndexEnd >= leftSplit && rowIndexEnd >= topSplit) {
		$renderFrom.push("render");
	}
	// if the span is fixed left
	if (colIndexStart < leftSplit) {
		$renderFrom.push("fixedCols");
	}
	// if the span isn't fixed left and fixed top
	if (rowIndexStart < topSplit && colIndexStart >= leftSplit) {
		$renderFrom.push("fixedRows");
	}
	return { ...span, $renderFrom };
}
